import React, { useEffect, useState } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Settings, BookmarkPlus, Trash2, X } from 'lucide-react';
import { setupTemplatesAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

// Boolean checkboxes rendered in the panel. Keep this list in sync with the
// snake_case column names on the SetupTemplate backend model.
const CHECKBOX_FIELDS = [
  { key: 'djTable', snake: 'dj_table', label: 'DJ Table' },
  { key: 'giftTable', snake: 'gift_table', label: 'Gift Table' },
  { key: 'headTable', snake: 'head_table', label: 'Head Table' },
  { key: 'cakeTable', snake: 'cake_table', label: 'Cake Table' },
  { key: 'registrationTable', snake: 'registration_table', label: 'Registration Table' },
  { key: 'dessertStation', snake: 'dessert_station', label: 'Dessert Station' },
  { key: 'waterStation', snake: 'water_station', label: 'Water Station' },
  { key: 'coffeeStation', snake: 'coffee_station', label: 'Coffee Station' },
  { key: 'barSetup', snake: 'bar_setup', label: 'Bar Setup' },
  { key: 'tournamentSetup', snake: 'tournament_setup', label: 'Tournament Set-Up' },
  { key: 'prizeTables', snake: 'prize_tables', label: 'Prize Tables' },
  { key: 'seeFloorplan', snake: 'see_floorplan', label: 'See Floorplan' },
  { key: 'saladStation', snake: 'salad_station', label: 'Salad Station' },
  { key: 'buffetLightUpWall', snake: 'buffet_light_up_wall', label: 'Buffet on Light Up Wall' },
  { key: 'buffetFrontOfRoom', snake: 'buffet_front_of_room', label: 'Buffet Front of Room' },
  { key: 'dessertBayWindow', snake: 'dessert_bay_window', label: 'Dessert Round in Bay Window' },
  { key: 'cocktailTables', snake: 'cocktail_tables', label: 'Cocktail Tables' },
  { key: 'podium', snake: 'podium', label: 'Podium' },
  { key: 'microphone', snake: 'microphone', label: 'Microphone' },
];

const SetupRequirementsSection = ({ formData, setFormData, isExpanded, onToggle }) => {
  const { toast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchTemplates = async () => {
    try {
      const list = await setupTemplatesAPI.getAll();
      setTemplates(list || []);
    } catch { /* silent — templates are a nice-to-have */ }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const applyTemplate = (templateId) => {
    const t = templates.find(x => x.id === templateId);
    if (!t) return;
    const patch = {
      tableStyle: t.table_style || formData.tableStyle,
      tableclothColor: t.tablecloth_color || formData.tableclothColor,
      napkinColor: t.napkin_color || formData.napkinColor,
    };
    CHECKBOX_FIELDS.forEach(({ key, snake }) => { patch[key] = !!t[snake]; });
    setFormData(prev => ({ ...prev, ...patch }));
    toast({ title: `Applied "${t.name}"` });
  };

  const buildTemplatePayload = () => {
    const payload = {
      name: templateName.trim(),
      description: templateDescription.trim() || null,
      table_style: formData.tableStyle || null,
      tablecloth_color: formData.tableclothColor || null,
      napkin_color: formData.napkinColor || null,
    };
    CHECKBOX_FIELDS.forEach(({ key, snake }) => { payload[snake] = !!formData[key]; });
    return payload;
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast({ title: 'Template name is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await setupTemplatesAPI.create(buildTemplatePayload());
      toast({ title: `Saved "${templateName.trim()}"` });
      setShowSaveModal(false);
      setTemplateName('');
      setTemplateDescription('');
      fetchTemplates();
    } catch (e) {
      toast({ title: 'Could not save template', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleDeleteTemplate = async (id, name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      await setupTemplatesAPI.delete(id);
      toast({ title: 'Template deleted' });
      fetchTemplates();
    } catch (e) {
      toast({ title: 'Could not delete template', variant: 'destructive' });
    }
  };

  return (
    <div className="border-b pb-4">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center justify-between w-full text-left font-semibold text-[#1e3a5f] mb-3"
      >
        <span className="flex items-center gap-2">
          <Settings size={18} />
          Setup Requirements (BEO)
        </span>
        <span className="text-sm text-gray-500">{isExpanded ? '−' : '+'}</span>
      </button>

      {isExpanded && (
        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
          {/* Template picker + Save row */}
          <div className="flex flex-wrap items-center gap-2 bg-white border rounded-md p-2">
            <span className="text-xs font-semibold text-[#1e3a5f] mr-1">Setup Template:</span>
            <Select onValueChange={applyTemplate}>
              <SelectTrigger className="h-8 text-xs w-56" data-testid="setup-template-select">
                <SelectValue placeholder={templates.length ? 'Apply a saved template…' : 'No templates saved yet'} />
              </SelectTrigger>
              <SelectContent>
                {templates.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-gray-500">Save your first template with the button →</div>
                ) : templates.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              type="button"
              onClick={() => setShowSaveModal(true)}
              data-testid="save-setup-template-btn"
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white"
            >
              <BookmarkPlus size={12} /> Save current as template
            </button>
            {templates.length > 0 && (
              <div className="flex flex-wrap items-center gap-1 ml-auto">
                {templates.map(t => (
                  <span key={t.id} className="inline-flex items-center gap-1 text-[10px] bg-gray-100 border rounded px-1.5 py-0.5">
                    {t.name}
                    <button
                      type="button"
                      title={`Delete "${t.name}"`}
                      onClick={() => handleDeleteTemplate(t.id, t.name)}
                      className="text-red-400 hover:text-red-600"
                      data-testid={`delete-setup-template-${t.id}`}
                    >
                      <Trash2 size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Table Style</Label>
              <Select
                value={formData.tableStyle}
                onValueChange={(value) => handleChange('tableStyle', value)}
              >
                <SelectTrigger data-testid="table-style-select">
                  <SelectValue placeholder="Select style" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="banquet">Banquet</SelectItem>
                  <SelectItem value="cocktail">Cocktail</SelectItem>
                  <SelectItem value="theatre">Theatre</SelectItem>
                  <SelectItem value="classroom">Classroom</SelectItem>
                  <SelectItem value="u-shape">U-Shape</SelectItem>
                  <SelectItem value="tournament_rounds">Tournament Rounds</SelectItem>
                  <SelectItem value="offsite">Offsite</SelectItem>
                  <SelectItem value="catering_pickup">Catering Pick Up</SelectItem>
                  <SelectItem value="catering_dropoff">Catering Drop Off</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tablecloth Color</Label>
              <Input
                data-testid="tablecloth-color-input"
                placeholder="e.g., White, Ivory"
                value={formData.tableclothColor}
                onChange={(e) => handleChange('tableclothColor', e.target.value)}
              />
            </div>
            <div>
              <Label>Napkin Color</Label>
              <Input
                data-testid="napkin-color-input"
                placeholder="e.g., Navy, Gold"
                value={formData.napkinColor}
                onChange={(e) => handleChange('napkinColor', e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Additional Table Requirements</Label>
            <div className="grid grid-cols-4 gap-3">
              {CHECKBOX_FIELDS.map(item => (
                <label
                  key={item.key}
                  className="flex items-center space-x-2 bg-white p-2 rounded border cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    data-testid={`${item.key}-checkbox`}
                    checked={formData[item.key]}
                    onChange={() => handleCheckboxChange(item.key)}
                    className="w-4 h-4 text-[#1e3a5f]"
                  />
                  <span className="text-sm">{item.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Save-as-template modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="save-setup-template-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="font-semibold text-[#1e3a5f]">Save Setup Template</h3>
              <button type="button" onClick={() => setShowSaveModal(false)} className="text-gray-400 hover:text-gray-700">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <Label htmlFor="setup-template-name">Template Name *</Label>
                <Input
                  id="setup-template-name"
                  data-testid="setup-template-name-input"
                  placeholder="e.g., Wedding Standard, Golf Tournament"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="setup-template-desc">Description (optional)</Label>
                <Input
                  id="setup-template-desc"
                  data-testid="setup-template-desc-input"
                  placeholder="Notes on when to use this template"
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                />
              </div>
              <p className="text-xs text-gray-500">
                Captures Table Style, Tablecloth &amp; Napkin colours, and every checkbox above.
              </p>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t bg-gray-50 rounded-b-xl">
              <button
                type="button"
                onClick={() => setShowSaveModal(false)}
                className="px-3 py-1.5 text-sm rounded border hover:bg-gray-100"
                disabled={saving}
              >Cancel</button>
              <button
                type="button"
                onClick={handleSaveTemplate}
                data-testid="save-setup-template-confirm"
                className="px-3 py-1.5 text-sm rounded bg-[#1e3a5f] text-white hover:bg-[#2d5016] disabled:opacity-60"
                disabled={saving}
              >{saving ? 'Saving…' : 'Save Template'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SetupRequirementsSection;
